import { groupBy, keyBy, sumBy } from 'lodash';
import { allSupplyChainPositions, GeolocationSupplyChainPosition } from '.';
import { siteTypeWeight } from './site-type-weights';

/** A fully covered supply-chain position, in the percentage unit site weights are stored in. */
export const FULL_COVERAGE_PERCENT = 100;

/**
 * Slack allowed above 100 before a position counts as over-weighted.
 *
 * Weights are doubles, so a set entered as exactly 100 can total 100.0001 — one
 * published set does — and refusing that would block a republish over a rounding
 * artefact. 0.01 is far below any total someone meant to type.
 */
const OVER_COVERAGE_TOLERANCE_PERCENT = 0.01;

/** The two fields weight resolution reads off a site. */
export type WeightedSite = {
  /** `company_geolocation_site.weight` — percent of this site's own position, or null for none. */
  storedWeight: number | null;
  supplyChainPosition: GeolocationSupplyChainPosition;
};

/**
 * How one supply-chain position's stored weights resolve (PLT-6333).
 *
 * Weights are shares of their own position, so each position is described — and
 * resolved — on its own. The input UI reports these states to the user and the
 * compute path divides by `divisor`; both read this one description so the rule
 * cannot drift between what a user is told and what is scored.
 *
 * Ask about a position through `exceedsFullCoverage` / `isPartlyWeighted` /
 * `isPartlyCovered` rather than comparing `kind` — see the note on those.
 */
export type PositionWeightCoverage = {
  supplyChainPosition: GeolocationSupplyChainPosition;
  siteCount: number;
  /** How many of the position's sites carry a stored weight. */
  weightedCount: number;
  /** Σ of the position's stored weights, in percent. */
  storedPercentSum: number;
  /**
   * - `equal-split` — no weight given, so the position's sites split 100 equally
   *   and it reads as fully covered by the sites listed for it.
   * - `partial-coverage` — every site weighted, totalling under 100: the listed
   *   sites cover only part of the position's footprint and are scored as such.
   * - `full-coverage` — every site weighted, totalling exactly 100.
   * - `normalised` — every site weighted, totalling over 100. **Refused at
   *   publish**, so this cannot arise from new input; the scaling-down below
   *   exists only so sets published before that check keep resolving to the
   *   numbers they always resolved to.
   * - `excluded` — every site weighted at 0, so the position contributes nothing.
   * - `partially-weighted` — some but not all sites weighted. Also refused at
   *   publish; older rows can still carry it, and resolve as an equal split.
   */
  kind: 'equal-split' | 'partial-coverage' | 'full-coverage' | 'normalised' | 'excluded' | 'partially-weighted';
  /**
   * What each stored weight is divided by, or null where there is no usable
   * divisor and the position falls back to an equal split — either because no
   * weight was stored, or because the stored total is not a finite number.
   */
  divisor: number | null;
};

/**
 * Describes each supply-chain position present in `sites`, in the canonical
 * Internal → Upstream → Downstream order. Positions with no sites are omitted.
 */
export function describeSiteWeightCoverage(sites: WeightedSite[]): PositionWeightCoverage[] {
  const sitesByPosition = groupBy(sites, site => site.supplyChainPosition);
  return allSupplyChainPositions
    .filter(position => (sitesByPosition[position] ?? []).length > 0)
    .map(position => describePosition(position, sitesByPosition[position]));
}

function describePosition(
  supplyChainPosition: GeolocationSupplyChainPosition,
  sitesOfPosition: WeightedSite[],
): PositionWeightCoverage {
  const siteCount = sitesOfPosition.length;
  const weightedCount = sitesOfPosition.filter(site => site.storedWeight !== null).length;
  const storedPercentSum = sumBy(sitesOfPosition, site => site.storedWeight ?? 0);
  const base = { supplyChainPosition, siteCount, weightedCount, storedPercentSum };

  if (weightedCount === 0) {
    return { ...base, kind: 'equal-split', divisor: null };
  }
  if (weightedCount < siteCount) {
    return { ...base, kind: 'partially-weighted', divisor: null };
  }
  if (storedPercentSum === 0) {
    // Every site deliberately zeroed. The divisor is irrelevant to the result
    // (every numerator is 0) but must not be 0 itself.
    return { ...base, kind: 'excluded', divisor: FULL_COVERAGE_PERCENT };
  }
  if (storedPercentSum > FULL_COVERAGE_PERCENT) {
    // A non-finite sum — reachable, since nothing bounds a weight from above and
    // the column is a DOUBLE — would divide every weight to 0 and silently drop
    // the position. Fall back to an equal split: absurd inputs get an even share
    // rather than none.
    return Number.isFinite(storedPercentSum)
      ? { ...base, kind: 'normalised', divisor: storedPercentSum }
      : { ...base, kind: 'normalised', divisor: null };
  }
  return {
    ...base,
    kind: storedPercentSum === FULL_COVERAGE_PERCENT ? 'full-coverage' : 'partial-coverage',
    divisor: FULL_COVERAGE_PERCENT,
  };
}

/**
 * The three questions the input path asks of a position, as predicates rather
 * than `kind` comparisons.
 *
 * `kind` names six states because `describePosition` distinguishes six, and each
 * name is worth having where the resolution is explained. But it is a describer's
 * output, not a protocol: callers matching on the strings couples every input gate
 * to the exact vocabulary, and the client and server halves of one gate then have
 * to keep agreeing about it by hand. That is how the over-coverage tolerance came
 * to be defined twice. These three are the whole consumer surface.
 */

/** Weights add up to more than the whole of the position — refused at publish. */
export function exceedsFullCoverage(coverage: PositionWeightCoverage): boolean {
  return coverage.storedPercentSum > FULL_COVERAGE_PERCENT + OVER_COVERAGE_TOLERANCE_PERCENT;
}

/**
 * Some but not all of the position's sites carry a weight — also refused at
 * publish, since the entered weights would be discarded for an equal split.
 */
export function isPartlyWeighted(coverage: PositionWeightCoverage): boolean {
  return coverage.kind === 'partially-weighted';
}

/**
 * Every site weighted, and the total is a real fraction of the position — under
 * full coverage but not zero. The state a suspiciously low total is judged
 * against; an all-zero position is deliberately excluded, being the only way to
 * leave a position out.
 */
export function isPartlyCovered(coverage: PositionWeightCoverage): boolean {
  return coverage.kind === 'partial-coverage';
}

/** How a position with no usable stored weights is split across its sites. */
export type UnweightedPositionSplit = 'equal' | 'site-type';

/** A site that also carries its type, which the `'site-type'` split reads. */
export type TypedWeightedSite = WeightedSite & {
  /** `company_geolocation_site.locationType`. */
  locationType: string | null;
};

/**
 * Resolves stored per-site weights into effective weights, independently within
 * each supply-chain position (PLT-6333).
 *
 * A stored weight is a site's **percentage share of its own supply-chain
 * position**, not of the company: Internal, Upstream and Downstream each run to
 * 100 on their own, so a company covering all three sums to 3 across positions.
 * Weighting them together would let one position dilute another — adding
 * supplier sites would lower the company's internal exposure — which is why the
 * positions never share a divisor.
 *
 * A position summing below 100 stays below 1 on purpose: the magnitude it feeds
 * is a weighted *sum*, so a position whose listed sites cover 70% of its
 * footprint contributes 70% of what full coverage would.
 *
 * Above 100 is refused by the input path (`exceedsFullCoverage`), so it cannot
 * arise from anything published since PLT-6333. Such a position still resolves
 * here — by scaling down to 1 — because sets published before that check has to
 * keep resolving to the numbers they resolve to today, rather than start
 * throwing. `describeSiteWeightCoverage` owns which case a position is in.
 *
 * Each returned weight is in [0, 1] and a position's weights sum to at most 1.
 */
export function resolveEffectiveSiteWeights<T extends WeightedSite>(sites: T[]): Array<T & { weight: number }>;
/**
 * As above, but choosing how a position with no usable stored weights is split
 * (PLT-7035): `'equal'` gives each site the same share; `'site-type'` gives each
 * site `siteTypeWeight / Σ siteTypeWeight` of the position, so a plant outweighs
 * the sales office next to it. Stored weights, where they resolve, always win —
 * the type split never mixes with them.
 *
 * The type split is opt-in per caller while it is decided which roll-ups should
 * use it; the climate FE / site-results pathway stays on `'equal'`.
 */
export function resolveEffectiveSiteWeights<T extends TypedWeightedSite>(
  sites: T[],
  unweightedSplit: UnweightedPositionSplit,
): Array<T & { weight: number }>;
export function resolveEffectiveSiteWeights<T extends WeightedSite & Partial<TypedWeightedSite>>(
  sites: T[],
  unweightedSplit: UnweightedPositionSplit = 'equal',
): Array<T & { weight: number }> {
  const coverageByPosition = keyBy(describeSiteWeightCoverage(sites), coverage => coverage.supplyChainPosition);
  const fallbackShareOf = (site: T): number => {
    const coverage = coverageByPosition[site.supplyChainPosition];
    if (unweightedSplit === 'equal') {
      return 1 / coverage.siteCount;
    }
    const positionTypeWeight = sumBy(
      sites.filter(other => other.supplyChainPosition === site.supplyChainPosition),
      other => siteTypeWeight(other.locationType),
    );
    return siteTypeWeight(site.locationType) / positionTypeWeight;
  };
  return sites.map(site => {
    const coverage = coverageByPosition[site.supplyChainPosition];
    return {
      ...site,
      weight: coverage.divisor === null ? fallbackShareOf(site) : (site.storedWeight ?? 0) / coverage.divisor,
    };
  });
}
